import React, { useEffect, useRef, useCallback } from 'react';
import { useUIStore } from '../stores/uiStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faGavel,
  faBolt,
  faArrowsRotate,
  faTrophy,
  faPeopleGroup,
  faChevronDown,
  faShieldHalved,
  faHandshake,
  faMobileScreen,
  faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import WebsiteNavbar from '../components/website/WebsiteNavbar';
import WebsiteFooter from '../components/website/WebsiteFooter';

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */
const LandingPage: React.FC = () => {
  const openModal = useUIStore((s) => s.openModal);

  /* ---------- parallax state ---------- */
  const scrollRef = useRef(0);
  const tickingRef = useRef(false);
  const parallaxContainerRef = useRef<HTMLDivElement>(null);

  // Refs for parallax layers
  const orbARef = useRef<HTMLDivElement>(null);
  const orbBRef = useRef<HTMLDivElement>(null);
  const orbCRef = useRef<HTMLDivElement>(null);
  const hexRef = useRef<HTMLDivElement>(null);
  const diamondRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const ctaOrbARef = useRef<HTMLDivElement>(null);
  const ctaOrbBRef = useRef<HTMLDivElement>(null);

  // Feature card refs
  const featureRefs = useRef<(HTMLDivElement | null)[]>([]);
  // How-it-works step refs
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  /* ---------- mobile detection ---------- */
  const isMobileRef = useRef(false);
  useEffect(() => {
    const check = () => { isMobileRef.current = window.innerWidth < 768; };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* ---------- parallax scroll handler ---------- */
  const applyParallax = useCallback(() => {
    const y = scrollRef.current;
    const factor = isMobileRef.current ? 0.3 : 1; // reduce on mobile

    // Hero orbs
    if (orbARef.current) {
      orbARef.current.style.transform = `translate3d(0, ${y * 0.15 * factor}px, 0)`;
    }
    if (orbBRef.current) {
      orbBRef.current.style.transform = `translate3d(0, ${y * -0.1 * factor}px, 0)`;
    }
    if (orbCRef.current) {
      orbCRef.current.style.transform = `translate3d(0, ${y * 0.08 * factor}px, 0)`;
    }
    // Geometric shapes
    if (hexRef.current) {
      hexRef.current.style.transform = `translate3d(${y * 0.04 * factor}px, ${y * -0.12 * factor}px, 0) rotate(${y * 0.04}deg)`;
    }
    if (diamondRef.current) {
      diamondRef.current.style.transform = `translate3d(${y * -0.06 * factor}px, ${y * 0.1 * factor}px, 0) rotate(${45 + y * 0.03}deg)`;
    }
    if (ringRef.current) {
      ringRef.current.style.transform = `translate3d(0, ${y * -0.07 * factor}px, 0) scale(${1 + y * 0.0002})`;
    }

    // CTA orbs
    if (ctaOrbARef.current) {
      const ctaOffset = Math.max(0, y - 2500);
      ctaOrbARef.current.style.transform = `translate3d(0, ${ctaOffset * -0.08 * factor}px, 0)`;
    }
    if (ctaOrbBRef.current) {
      const ctaOffset = Math.max(0, y - 2500);
      ctaOrbBRef.current.style.transform = `translate3d(0, ${ctaOffset * 0.06 * factor}px, 0)`;
    }

    tickingRef.current = false;
  }, []);

  useEffect(() => {
    const onScroll = () => {
      scrollRef.current = window.scrollY;
      if (!tickingRef.current) {
        tickingRef.current = true;
        requestAnimationFrame(applyParallax);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [applyParallax]);

  /* ---------- intersection observer for feature cards ---------- */
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('animate-in');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    featureRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* ---------- intersection observer for how-it-works steps ---------- */
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('step-in');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -40px 0px' }
    );
    stepRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* ---------- data ---------- */
  const highlights = [
    {
      icon: faShieldHalved,
      title: 'Free to Play',
      description: 'No entry fees, no in-app purchases. Pure skill-based competition with your friends.',
    },
    {
      icon: faHandshake,
      title: 'Play with Friends',
      description: 'Private leagues with invite codes. The bragging rights are the real prize.',
    },
    {
      icon: faMobileScreen,
      title: 'Web & Mobile',
      description: 'Play anywhere — on your phone or desktop. Your teams sync across all devices.',
    },
    {
      icon: faChartLine,
      title: 'Ball-by-Ball Updates',
      description: 'Live scoring from real matches. Every run, wicket, and catch counts.',
    },
  ];

  const features = [
    {
      icon: faUsers,
      title: 'Private Leagues',
      description:
        'Create exclusive leagues, invite friends with a code, and settle the ultimate debate of who knows cricket best.',
    },
    {
      icon: faGavel,
      title: 'Live Auctions',
      description:
        'Experience the thrill of real-time player auctions. Bid, strategize, and build your squad under pressure.',
    },
    {
      icon: faBolt,
      title: 'Real-time Scoring',
      description:
        'Watch your points update ball-by-ball. Live leaderboards keep every delivery exciting and every over dramatic.',
    },
    {
      icon: faArrowsRotate,
      title: 'Smart Transfers',
      description:
        'Make tactical transfers between matches. Read the form, predict the pitch, and stay ahead of the competition.',
    },
  ];

  const ownerSteps = [
    { icon: faShieldHalved, title: 'Create a Contest', description: 'Pick a series, name your contest, and choose a plan that fits your league.' },
    { icon: faGavel, title: 'Set Rules & Scoring', description: 'Configure team size, role limits, scoring system, transfers, and captaincy rules.' },
    { icon: faUsers, title: 'Publish & Invite', description: 'Publish your contest to generate a join code, then share it with friends.' },
  ];

  const playerSteps = [
    { icon: faHandshake, title: 'Join with a Code', description: 'Enter the contest code shared by the owner to request to join the league.' },
    { icon: faPeopleGroup, title: 'Build Your Dream Team', description: 'Pick your best XI, choose your captain and vice-captain wisely.' },
    { icon: faTrophy, title: 'Compete & Win', description: 'Earn points from real matches, make transfers, and climb the leaderboard.' },
  ];

  return (
    <div ref={parallaxContainerRef} className="min-h-screen bg-[#0a0f0d] overflow-x-hidden">
      {/* Inline keyframe styles for scroll-triggered animations */}
      <style>{`
        .feature-card {
          opacity: 0;
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .feature-card.from-left {
          transform: translate3d(-80px, 30px, 0);
        }
        .feature-card.from-right {
          transform: translate3d(80px, 30px, 0);
        }
        .feature-card.animate-in {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }

        .step-card {
          opacity: 0;
          transform: translate3d(0, 50px, 0);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .step-card.step-in {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }

        @keyframes float-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-18px); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        @keyframes pulse-ring {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.08); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-float-slow { animation: float-slow 6s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 4s ease-in-out infinite; }
        .animate-pulse-ring { animation: pulse-ring 5s ease-in-out infinite; }

        .connecting-line {
          background: linear-gradient(180deg, transparent 0%, #00FF85 50%, transparent 100%);
        }
      `}</style>

      <WebsiteNavbar />

      {/* ============================================================ */}
      {/*  HERO                                                         */}
      {/* ============================================================ */}
      <section className="relative pt-28 sm:pt-40 pb-28 sm:pb-36 px-4 overflow-hidden min-h-[90vh] flex items-center">
        {/* --- Parallax decorative layers --- */}
        {/* Gradient orb A - top right */}
        <div
          ref={orbARef}
          className="absolute -top-20 -right-32 w-[500px] h-[500px] rounded-full will-change-transform pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,133,0.12) 0%, transparent 70%)',
          }}
        />
        {/* Gradient orb B - bottom left */}
        <div
          ref={orbBRef}
          className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full will-change-transform pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,133,0.08) 0%, transparent 70%)',
          }}
        />
        {/* Small accent orb C */}
        <div
          ref={orbCRef}
          className="absolute top-1/3 left-[15%] w-[200px] h-[200px] rounded-full will-change-transform pointer-events-none hidden md:block"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,133,0.06) 0%, transparent 70%)',
          }}
        />

        {/* Hexagonal shape */}
        <div
          ref={hexRef}
          className="absolute top-[18%] right-[12%] will-change-transform pointer-events-none hidden md:block"
        >
          <div className="w-20 h-20 border-2 border-[#00FF85]/15 rotate-12 animate-float-slow"
            style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}
          />
        </div>

        {/* Diamond shape */}
        <div
          ref={diamondRef}
          className="absolute bottom-[25%] left-[8%] will-change-transform pointer-events-none hidden md:block"
        >
          <div className="w-14 h-14 border-2 border-[#00FF85]/10 rotate-45 animate-float-medium" />
        </div>

        {/* Ring */}
        <div
          ref={ringRef}
          className="absolute top-[60%] right-[20%] will-change-transform pointer-events-none hidden md:block"
        >
          <div className="w-32 h-32 rounded-full border-2 border-[#00FF85]/10 animate-pulse-ring" />
        </div>

        {/* Dot grid pattern - subtle */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(circle, #00FF85 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        {/* --- Hero content --- */}
        <div className="relative z-10 max-w-5xl mx-auto text-center w-full">
          <div className="inline-block mb-6 px-4 py-1.5 border border-[#00FF85]/20 rounded-full bg-[#00FF85]/5">
            <span className="text-[#00FF85] text-xs sm:text-sm font-semibold tracking-wide uppercase">
              Fantasy Cricket Platform
            </span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-8xl font-extrabold text-white leading-[1.05] mb-6">
            Fantasy Cricket,{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: 'linear-gradient(135deg, #00FF85 0%, #00cc6a 50%, #00FF85 100%)',
              }}
            >
              Reimagined
            </span>
          </h1>

          <p className="text-gray-400 text-lg sm:text-xl lg:text-2xl mb-10 max-w-2xl mx-auto leading-relaxed">
            Build your dream team, compete with friends in private leagues, and experience the thrill of every delivery.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <button
              onClick={() => openModal('auth-register')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00FF85] text-black rounded-xl text-base font-bold hover:bg-[#00E077] hover:scale-[1.03] transition-all shadow-lg shadow-[#00FF85]/20 w-full sm:w-auto"
            >
              Create Account
            </button>
            <button
              onClick={() => openModal('auth-login')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/15 text-white rounded-xl text-base font-bold hover:border-[#00FF85]/40 hover:text-[#00FF85] transition-all w-full sm:w-auto"
            >
              Login
            </button>
          </div>

          {/* Scroll indicator */}
          <div className="animate-bounce mt-8">
            <FontAwesomeIcon icon={faChevronDown} className="text-gray-600 text-lg" />
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  WHY CRICCLASH                                                */}
      {/* ============================================================ */}
      <section className="relative py-16 sm:py-20 px-4 border-y border-[#1a3a2a] bg-[#0d1310]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h, i) => (
              <div
                key={i}
                className="text-center px-4 py-6 rounded-xl border border-white/[0.04] hover:border-[#00FF85]/20 hover:bg-white/[0.02] transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-full bg-[#00FF85]/10 flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon icon={h.icon} className="text-[#00FF85] text-lg" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{h.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{h.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FEATURES                                                     */}
      {/* ============================================================ */}
      <section className="py-20 sm:py-28 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Packed with{' '}
              <span className="text-[#00FF85]">powerful</span> features
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              Everything you need to dominate your fantasy cricket league, all in one platform.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
            {features.map((f, i) => (
              <div
                key={i}
                ref={(el) => { featureRefs.current[i] = el; }}
                className={`feature-card ${i % 2 === 0 ? 'from-left' : 'from-right'} group relative bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-7 sm:p-8 hover:border-[#00FF85]/25 hover:bg-white/[0.05] transition-all duration-300`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* Hover glow */}
                <div className="absolute inset-0 rounded-2xl bg-[#00FF85]/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-[#00FF85]/10 border border-[#00FF85]/20 flex items-center justify-center text-[#00FF85] mb-5 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-[#00FF85]/10 transition-all duration-300">
                    <FontAwesomeIcon icon={f.icon} className="text-xl" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2">{f.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  HOW IT WORKS                                                 */}
      {/* ============================================================ */}
      <section className="py-20 sm:py-28 px-4 bg-[#0d1310]/60 border-y border-[#1a3a2a] relative overflow-hidden">
        {/* Subtle background pattern */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.015]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,255,133,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,133,1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              How it{' '}
              <span className="text-[#00FF85]">works</span>
            </h2>
            <p className="text-gray-500 text-lg max-w-lg mx-auto">
              Two paths, one goal — build the ultimate fantasy cricket experience.
            </p>
          </div>

          {/* Two-column flow */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Owner Flow */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#00FF85]/10 border border-[#00FF85]/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faShieldHalved} className="text-[#00FF85]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Contest Owner</h3>
                  <p className="text-xs text-gray-500">Create & manage your league</p>
                </div>
              </div>
              <div className="space-y-4">
                {ownerSteps.map((s, i) => (
                  <div
                    key={i}
                    ref={(el) => { stepRefs.current[i] = el; }}
                    className="step-card"
                    style={{ transitionDelay: `${i * 150}ms` }}
                  >
                    <div className="flex gap-4 items-start">
                      <div className="flex flex-col items-center shrink-0">
                        <div className="w-10 h-10 rounded-full bg-[#00FF85]/10 border border-[#00FF85]/20 flex items-center justify-center">
                          <span className="text-[#00FF85] text-sm font-bold">{i + 1}</span>
                        </div>
                        {i < ownerSteps.length - 1 && <div className="w-px h-8 bg-[#00FF85]/10 mt-1" />}
                      </div>
                      <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <FontAwesomeIcon icon={s.icon} className="text-[#00FF85] text-xs" />
                          <h4 className="text-white font-semibold text-sm">{s.title}</h4>
                        </div>
                        <p className="text-gray-500 text-xs leading-relaxed">{s.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Player Flow */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faTrophy} className="text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Player</h3>
                  <p className="text-xs text-gray-500">Join, compete & win</p>
                </div>
              </div>
              <div className="space-y-4">
                {playerSteps.map((s, i) => (
                  <div
                    key={i}
                    ref={(el) => { stepRefs.current[i + 3] = el; }}
                    className="step-card"
                    style={{ transitionDelay: `${(i + 3) * 150}ms` }}
                  >
                    <div className="flex gap-4 items-start">
                      <div className="flex flex-col items-center shrink-0">
                        <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                          <span className="text-amber-400 text-sm font-bold">{i + 1}</span>
                        </div>
                        {i < playerSteps.length - 1 && <div className="w-px h-8 bg-amber-500/10 mt-1" />}
                      </div>
                      <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <FontAwesomeIcon icon={s.icon} className="text-amber-400 text-xs" />
                          <h4 className="text-white font-semibold text-sm">{s.title}</h4>
                        </div>
                        <p className="text-gray-500 text-xs leading-relaxed">{s.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FINAL CTA                                                    */}
      {/* ============================================================ */}
      <section className="relative py-24 sm:py-32 px-4 overflow-hidden">
        {/* Parallax gradient orbs */}
        <div
          ref={ctaOrbARef}
          className="absolute -top-32 -right-32 w-[450px] h-[450px] rounded-full will-change-transform pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,133,0.1) 0%, transparent 70%)',
          }}
        />
        <div
          ref={ctaOrbBRef}
          className="absolute -bottom-24 -left-24 w-[350px] h-[350px] rounded-full will-change-transform pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,133,0.07) 0%, transparent 70%)',
          }}
        />

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: 'linear-gradient(135deg, #ffffff 0%, #00FF85 50%, #00cc6a 100%)',
              }}
            >
              Ready to Play?
            </span>
          </h2>
          <p className="text-gray-400 text-lg sm:text-xl mb-10 max-w-lg mx-auto leading-relaxed">
            Join thousands of cricket fans already competing on CricClash. Your dream team awaits.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => openModal('auth-register')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00FF85] text-black rounded-xl text-base font-bold hover:bg-[#00E077] hover:scale-[1.03] transition-all shadow-lg shadow-[#00FF85]/20 w-full sm:w-auto"
            >
              Create Account
            </button>
            <button
              onClick={() => openModal('auth-login')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/15 text-white rounded-xl text-base font-bold hover:border-[#00FF85]/40 hover:text-[#00FF85] transition-all w-full sm:w-auto"
            >
              Login
            </button>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
};

export default LandingPage;
